import React from 'react';
import className from 'classnames';
import { displayPaymentMethod } from '../lib/payment_method_icons';
import { PaymentMethodIcons } from './payment_method_icons';
import {
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
  core_apimessages_Error as Error,
  StartCheckoutForListing,
} from '../schemas/rql';
import gql from 'graphql-tag';
import { MutationFn } from 'react-apollo';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { useMutation } from '../lib/use_mutation_hook';
import { trackEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

type StartCheckoutForListingMutationFn = MutationFn<
StartCheckoutForListing.Mutation,
StartCheckoutForListing.Variables
>;

interface MutationProps {
  startCheckoutForListing: StartCheckoutForListingMutationFn;
}

interface ExternalProps {
  paymentMethod: CheckoutPaymentMethodType;
  listingId: string;
  onSuccess: (redirectUrl: string, paymentMethodType: CheckoutPaymentMethodType) => any;
  onError: (errors: Error[]) => any;
  setLoading: (loading: boolean) => any;
  isDisabled: boolean;
}

type IProps = ExternalProps & MutationProps;

export function PaymentSelectionModalPaymentMethod(props: IProps) {
  const user = useUser();

  const [mutate, { errors }] = useMutation(props.startCheckoutForListing);
  const { onError, setLoading } = props;

  React.useEffect(() => {
    if (errors.length) {
      onError(errors);
      setLoading(false);
    }
  }, [onError, errors, setLoading]);

  const display = displayPaymentMethod(props.paymentMethod);
  if (!display || props.isDisabled) return null;

  const onClickPaymentMethod = async () => {
    const customTrackedValues = { userId: user.id, paymentMethod: props.paymentMethod };
    trackEvent({
      eventName: MParticleEventName.ClickedBuyItNowPaymentMethod,
      listingId: props.listingId,
      ...customTrackedValues,
    });

    props.setLoading(true);

    const result = await mutate({
      variables: {
        input: {
          listingId: props.listingId,
          paymentMethod: props.paymentMethod,
        },
      },
    });

    const redirectUrl = result?.data?.startCheckoutForListing?.redirectUrl;

    if (redirectUrl) {
      props.onSuccess(redirectUrl, props.paymentMethod);
    }
  };

  const buttonClasses = className(
    'payment-method-button',
    { 'payment-method-button--dc': props.paymentMethod === CheckoutPaymentMethodType.DIRECT_CHECKOUT },
  );

  return (
    <button
      onClick={onClickPaymentMethod}
      type="button"
      className={buttonClasses}
    >
      <h3 className="payment-method-button__title">
        {display.title}
      </h3>
      <div className="payment-method-button__icons">
        <PaymentMethodIcons icons={display.icons} />
      </div>
    </button>
  );
}

const withMutation = withGraphql<
ExternalProps,
StartCheckoutForListing.Mutation,
StartCheckoutForListing.Variables
>(
  gql`
  mutation StartCheckoutForListing($input: Input_core_apimessages_StartCheckoutForListingRequest) {
    startCheckoutForListing(input: $input) {
      redirectUrl
    }
  }`,
  {
    name: 'startCheckoutForListing',
  },
);

export default withMutation(PaymentSelectionModalPaymentMethod);
